import CloseIcon from '@/components/Icon/CloseIcon';
import ExpandIcon from '@/components/Icon/ExpandIcon';
import IconButton from '@/components/IconButton/IconButton';
import LabelBackground from '@/components/LabelBackground/LabelBackground';
import useUrlHistorySyncedOpenState from '@/hooks/useUrlHistorySyncedOpenState';
import cn from '@/lib/cs';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { motion, useReducedMotion, useWillChange } from 'framer-motion';
import { MouseEventHandler, useId } from 'react';

const ExpandableImage = ({
	src,
	thumbnailSrc,
	alt,
	caption,
	className,
	imageClassName,
	onClick,
	children,
	...props
}: Omit<
	Omit<React.ComponentProps<typeof motion.div>, 'children'>,
	'onClick'
> & {
	src: string;
	thumbnailSrc: string;
	alt: string;
	caption?: string | null;
	imageClassName?: string;
	children?: React.ReactNode;
	onClick?: MouseEventHandler<HTMLButtonElement | HTMLDivElement>;
}) => {
	const layoutId = useId();
	const reduceMotion = useReducedMotion();
	const willChange = useWillChange();

	const [isExpanded, setIsExpanded] = useUrlHistorySyncedOpenState();

	return (
		<>
			<motion.div
				className={cn('relative block h-44 min-w-40', className)}
				tabIndex={-1}
				onClick={(event) => {
					onClick?.(event);
					setIsExpanded(true);
				}}
				whileTap={{
					scale: 0.95,
				}}
				{...props}>
				<motion.div
					layoutId={reduceMotion ? undefined : `${layoutId}-image`}
					layoutDependency={isExpanded}
					className="h-full bg-white"
					style={{
						WebkitMaskImage: `url("/assets/masks/blob_square_2.svg")`,
						maskImage: `url("/assets/masks/blob_square_2.svg")`,
						maskSize: 'fill',
						maskRepeat: 'no-repeat',
						willChange,
					}}>
					<motion.div
						layoutId={reduceMotion ? undefined : layoutId}
						layoutDependency={isExpanded}
						className="absolute inset-0 size-full bg-white"
						style={{
							willChange,
						}}
						animate={{
							opacity: isExpanded ? 0 : 1,
							transition: {
								delay: isExpanded ? 1 : 0,
							},
						}}
					/>
					{!!thumbnailSrc && (
						<motion.img
							src={thumbnailSrc}
							alt=""
							className={cn(
								`relative z-10 size-full object-cover`,
								imageClassName,
							)}
						/>
					)}
					<motion.img
						src={src}
						alt={alt}
						className={cn(
							`absolute inset-0 z-10 size-full object-cover`,
							imageClassName,
						)}
					/>
				</motion.div>
				{children}
				{!!caption && (
					<motion.p
						layoutId={
							reduceMotion ? undefined : `${layoutId}-caption`
						}
						layoutDependency={isExpanded}
						className="absolute -bottom-2 -start-1 text-xl text-gray-50"
						style={{
							willChange,
						}}>
						<span className="relative block rotate-2 px-2 pb-1 pt-0.5">
							<LabelBackground className="text-gray-950" />
							<span className="relative z-10 line-clamp-1 max-w-full text-ellipsis break-words">
								{caption}
							</span>
						</span>
					</motion.p>
				)}
				<ExpandButton
					layoutId={reduceMotion ? undefined : `${layoutId}-button`}
					layoutDependency={isExpanded}
					className="-end-4 bottom-5"
					onClick={(event) => {
						event.stopPropagation();
						onClick?.(event);
						setIsExpanded(true);
					}}
				/>
			</motion.div>
			<Dialog
				layoutId={reduceMotion ? undefined : layoutId}
				layoutDependency={isExpanded}
				as={motion.div}
				open={isExpanded}
				onClose={() => setIsExpanded(false)}
				className="fixed inset-0 z-50 size-full bg-white text-gray-950 backdrop-blur-md">
				<DialogPanel
					className="flex size-full min-h-0 flex-col items-stretch justify-stretch"
					onClick={(event) => {
						event.stopPropagation();
						setIsExpanded(false);
					}}>
					<DialogTitle className="shrink-0 pt-2 text-center font-brand text-2xl">
						Afbeelding
					</DialogTitle>
					<div className="fixed left-1/2 top-12 z-20 -translate-x-1/2">
						<IconButton
							layoutId={
								reduceMotion ? undefined : `${layoutId}-button`
							}
							layoutDependency={isExpanded}
							onClick={(event) => {
								event.stopPropagation();
								setIsExpanded(false);
							}}
							aria-label="Sluiten"
							title="Sluiten"
							icon={<CloseIcon />}
						/>
					</div>
					<motion.img
						layoutId={
							reduceMotion ? undefined : `${layoutId}-image`
						}
						layoutDependency={isExpanded}
						src={src}
						alt={alt}
						className="size-full min-h-64 grow object-scale-down object-center"
						style={{
							willChange,
						}}
					/>
					{!!caption && (
						<motion.p
							layoutId={
								reduceMotion ? undefined : `${layoutId}-caption`
							}
							layoutDependency={isExpanded}
							style={{
								willChange,
							}}
							className="absolute bottom-4 start-4 z-10 font-brand text-2xl text-gray-50 xs:text-3xl sm:text-4xl">
							<span className="relative block max-w-80 px-3 py-1 xs:max-w-96 sm:max-w-none">
								<LabelBackground className="text-gray-950" />
								<span className="relative z-10 break-words">
									{caption}
								</span>
							</span>
						</motion.p>
					)}
				</DialogPanel>
			</Dialog>
		</>
	);
};

export const ExpandButton = ({
	layoutId,
	layoutDependency,
	className,
	onClick,
}: {
	layoutId?: string;
	layoutDependency?: boolean;
	className?: string;
	onClick: MouseEventHandler<HTMLButtonElement>;
}) => (
	<IconButton
		layoutId={layoutId}
		layoutDependency={layoutDependency}
		className={cn(`absolute z-20`, className)}
		icon={<ExpandIcon className="h-6 lg:h-8" />}
		aria-label="Groter bekijken"
		title="Groter bekijken"
		onClick={onClick}
	/>
);

export default ExpandableImage;
